import { Box, VBox } from "@/components-v2/Box";
import { Button, CloseButton, ToggleButton } from "@/components-v2/Button";
import { DataTable } from "@/components-v2/DataTable";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { EmptyState } from "@/components-v2/EmptyState";
import { FullScreenLayout, PageHeader } from "@/components-v2/FullScreenLayout";
import { Tag } from "@/components-v2/Tag";
import { IncreaseNominalValue2 } from "@/dbot/common/commonCapitalIncreaseComponents";
import { SelectActorsDialog } from "@/dialogs/dialogComponents";
import { useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { CURRENCY_NOK } from "@/utils/currency";
import { whenNotEmpty } from "@/utils/functions";
import { observer } from "mobx-react";
import { ReactElement } from "react";
import { IncreaseNominalValueShareContribution2 } from "./IncreaseNominalValue";
import { ShareCapitalSummaryTable } from "./increaseOfShareCapitalComponents";
import {
  IncreaseOfShareCapitalForm,
  IncreaseOfShareCapitalType,
  prepAddSubscribersDropdown,
  SubscribersDialogState,
} from "./increaseOfShareCapitalDomain";
import { SelectShareTypesForIssue2 } from "./SelectShareTypesForIssue";
import { ShareCapitalIncreaseMethod2 } from "./ShareCapitalIncreaseMethod";
import { ShareIssueOrIncreaseNominalValue2 } from "./ShareIssueOrIncreaseNominalValue";
import { PreferentialRightQuestion2 } from "./ShowPreferentialRight";
import {
  closeSubscribersDialog,
  editSubscribersWithExcel,
  importSubscribersFileUploadConfig,
  importSubscribersFromExcel,
  isEmptyState,
  leftPanels,
  prepIncreaseNominalValueSubscribersTable,
  prepShareIssueSubscribersTable,
  prepShortcutButtons,
  toggleLeftPanel,
} from "./subscribersDialogDomain";
import { Warning } from "./Warning";

export const SubscribersTitle = observer(({ form }: { form: IncreaseOfShareCapitalForm }) => {
  const { t } = useTranslation();
  return (
    <Box alignItems="center">
      {t(
        form.isShareIssue
          ? "decisions.increaseShareCapital.manageSubscribers.issue"
          : "decisions.increaseShareCapital.manageSubscribers",
      )}
      <Tag {...form.calculatedSubscriptionProgress} />
    </Box>
  );
});

export const SubscribersDialog = observer(
  ({
    form,
    state,
    subDialogs,
  }: {
    form: IncreaseOfShareCapitalForm;
    state: SubscribersDialogState;
    subDialogs?: ReactElement;
  }) => {
    const { t } = useTranslation();
    const i18n = useRootStore().i18n;

    return (
      <Dialog overlay>
        <FullScreenLayout
          pageHeader={
            <PageHeader
              title={<SubscribersTitle form={form} />}
              onGoBack={() => closeSubscribersDialog(form)}
              onGoBackVariant={"x"}
              menu={
                <Box gap={"lg"}>
                  {form.increaseOfShareCapitalTypeModel.value ===
                    IncreaseOfShareCapitalType.shareIssue && (
                    <>
                      <DropdownMenu
                        {...prepAddSubscribersDropdown({ form, variant: "link", alignment: "end" })}
                      />
                      <DropdownMenu
                        button={{
                          type: "link",
                          label: t("excel.edit-with-excel"),
                        }}
                        items={[
                          {
                            type: "item",
                            label: t("excel.download"),
                            onSelect: () => editSubscribersWithExcel(form, i18n),
                            icon: "download",
                          },
                          {
                            type: "file-upload",
                            label: t("excel.upload"),
                            onSelect: (files, fileUploadConfig) =>
                              importSubscribersFromExcel(form, files, fileUploadConfig, i18n),
                            fileUploadConfig: importSubscribersFileUploadConfig(form),
                          },
                        ]}
                      />
                    </>
                  )}

                  <Button
                    variant={"secondary"}
                    label={t("close")}
                    onClick={() => closeSubscribersDialog(form)}
                  />
                </Box>
              }
            />
          }
          leftPanelContainerProps={{
            vertical: false,
            overflow: "hidden",
            gap: false,
            borderRight: false,
            flex: "none",
          }}
          leftPanel={
            <>
              <VBox padding borderRight gap={"md"}>
                {Object.values(leftPanels).map((p) => (
                  <ToggleButton
                    key={p.name}
                    variant={"basic"}
                    icon={p.icon}
                    tooltip={p.title}
                    disableMinWidth
                    selected={state.leftPanel === p.name}
                    onClick={() => toggleLeftPanel(form, p.name)}
                  />
                ))}
              </VBox>
              {state.leftPanel && (
                <VBox gap={0} overflowY={"hidden"} bg={"base"} grow borderRight className="w-136">
                  <VBox gap={0} bg borderBottom>
                    <Box
                      paddingX
                      paddingY={"sm"}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <span>{t(leftPanels[state.leftPanel].title)}</span>
                      <CloseButton color="inherit" onClick={() => toggleLeftPanel(form)} />
                    </Box>
                  </VBox>
                  <VBox overflowY={"auto"}>
                    {state.leftPanel === "settings" ? (
                      <SubscribersSettings form={form} />
                    ) : state.leftPanel === "key-figures" ? (
                      <SubscribersKeyFigures form={form} />
                    ) : undefined}
                  </VBox>
                </VBox>
              )}
            </>
          }
          centerPanel={
            isEmptyState(form) ? (
              <VBox padding grow={"golden"} justifyContent={"center"}>
                <SubscribersEmptyState form={form} />
              </VBox>
            ) : (
              <VBox padding>
                <ShareIssueSubscribersTable form={form} />
              </VBox>
            )
          }
        />
        {form.shareIssue.addShareholdersFromActorsModel && (
          <SelectActorsDialog model={form.shareIssue.addShareholdersFromActorsModel} />
        )}
        {subDialogs}
      </Dialog>
    );
  },
);

const SubscribersSettings = observer(({ form }: { form: IncreaseOfShareCapitalForm }) => {
  const { t } = useTranslation();
  return (
    <VBox padding gap={"lg"}>
      <ShareIssueOrIncreaseNominalValue2 form={form} />

      {form.increaseOfShareCapitalTypeModel.value === IncreaseOfShareCapitalType.shareIssue ? (
        <SelectShareTypesForIssue2
          model={form.shareIssue}
          disableIndividualSharePrice={form.isPreferentialRight}
        />
      ) : form.increaseOfShareCapitalTypeModel.value ===
        IncreaseOfShareCapitalType.increaseNominalValue ? (
        <>
          <IncreaseNominalValue2
            increaseInNominalValue={form.increaseNominalValue.increaseInNominalValue}
            currentNominalValue={form.currentNominalValue}
            currentNumberOfShares={form.currentNumberOfShares}
            isProjectLocked={form.isProjectLocked}
          />
          {!form.isNewNominalValueValid && (
            <Warning
              message={t(
                "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.newNominal-helperText",
              )}
            />
          )}
          {form.isNewNominalValueValid && !form.isNewShareCapitalValid && (
            <Warning
              message={t(
                "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.newShareCapital-helperText",
              )}
            />
          )}
          {form.showIncreaseContributionPerShare && (
            <IncreaseNominalValueShareContribution2 form={form} />
          )}
        </>
      ) : null}

      <ShareCapitalIncreaseMethod2 form={form} />
      <PreferentialRightQuestion2 form={form} />
    </VBox>
  );
});

const SubscribersKeyFigures = observer(({ form }: { form: IncreaseOfShareCapitalForm }) => {
  const { t, f } = useTranslation();
  return (
    <VBox padding gap={"xl"}>
      {form.isShareCapitalIncreaseRange ? (
        <VBox gap={"md"}>
          <Box>
            {t({
              type: "list",
              value: [
                { key: "decisionType.increaseOfShareCapital.name" },
                { key: "decisions.increaseShareCapital.summary.minMaxSuffix" },
              ],
              separator: " ",
            })}
          </Box>
          <Box textSize={"h3"}>
            {f.formatAmount(CURRENCY_NOK, form.getIncreaseInShareCapital("minimum"))} -{" "}
            {f.formatAmount(CURRENCY_NOK, form.getIncreaseInShareCapital("maximum"))}
          </Box>
        </VBox>
      ) : (
        <VBox gap={"md"}>
          <Box>{t("decisionType.increaseOfShareCapital.name")}</Box>
          <Box textSize={"h3"}>
            {f.formatAmount(CURRENCY_NOK, form.getIncreaseInShareCapital("minimum"))}
          </Box>
        </VBox>
      )}
      <ShareCapitalSummaryTable form={form} />
    </VBox>
  );
});

const ShareIssueSubscribersTable = observer(({ form }: { form: IncreaseOfShareCapitalForm }) => {
  switch (form.increaseOfShareCapitalTypeModel.value) {
    case IncreaseOfShareCapitalType.increaseNominalValue:
      return <DataTable {...prepIncreaseNominalValueSubscribersTable(form)} />;
    case IncreaseOfShareCapitalType.shareIssue:
      return <DataTable {...prepShareIssueSubscribersTable(form)} />;
  }
});

const SubscribersEmptyState = observer(({ form }: { form: IncreaseOfShareCapitalForm }) => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={"grid"}
      actions={
        <VBox gap={"lg"}>
          <div>
            <DropdownMenu {...prepAddSubscribersDropdown({ form, variant: "primary" })} />
          </div>
          {whenNotEmpty(prepShortcutButtons(form), (buttons) => (
            <>
              <div>{t("general.shortcuts")}</div>
              <Box wrap justifyContent="center" gap={"md"}>
                {buttons.map(
                  ([name, props]) => props && <Button key={name} size="sm" {...props} />,
                )}
              </Box>
            </>
          ))}
        </VBox>
      }
    />
  );
});
